// The dashboard's side of the HACCP offline queue (lib/haccpQueue.ts, docs/architecture.md §22.8): one queue per tab
// over IndexedDB, how each record type is sent, when the queue is sent (the network back, the tab back in front, every
// 20 seconds while records wait), and the hooks the forms and the HACCP header use.
//
// Records belong to the user who entered them and to an organisation: the queue sends only the signed-in user's
// records, to the organisations they belong to, and shows only theirs. Signing out keeps them on the phone (they are
// the user's records, sent at their next sign-in); another user signing in on the same phone neither sees nor sends them.

import type { CleaningCompletionInput, CoolingCycleInput, ReceptionInput } from '@cck/schema';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { uploadFile } from './api.ts';
import {
  HaccpQueue, lazyStore, type NewRecord, openStore, type QueuedRecord, type Scope, type SubmitResult,
} from './haccpQueue.ts';
import { keys, orgApi } from './queries.ts';
import { receptionApi } from './receptionQueries.ts';

/** Sends the queue every this often while records wait (each record also has its own backoff). */
const TICK_MS = 20_000;

type Saved = (orgIds: string[]) => void;
const savedListeners = new Set<Saved>();

const webLock = <T>(fn: () => Promise<T>): Promise<T> =>
  typeof navigator !== 'undefined' && navigator.locks
    ? navigator.locks.request('cck-haccp-queue', fn) as Promise<T>
    : fn();

export const haccpQueue = new HaccpQueue(lazyStore(openStore), {
  upload: (orgId, blob, purpose) => uploadFile(orgId, blob, purpose),
  create: (r) => {
    if (r.kind === 'cooling') return orgApi(r.orgId).startCooling(r.body as unknown as CoolingCycleInput);
    if (r.kind === 'reception') return receptionApi(r.orgId).createReception(r.body as unknown as ReceptionInput);
    return orgApi(r.orgId).completeCleaning(r.body as unknown as CleaningCompletionInput);
  },
}, {
  online: () => navigator.onLine,
  lock: webLock,
  onSaved: (orgIds) => { for (const fn of savedListeners) fn(orgIds); },
});

/** The user's records in the queue, all organisations together (the sign-out warning). */
export function queuedFor(userId: string): QueuedRecord[] {
  return haccpQueue.snapshot().filter((r) => r.userId === userId);
}

function useOnline(): boolean {
  return useSyncExternalStore((fn) => {
    window.addEventListener('online', fn);
    window.addEventListener('offline', fn);
    return () => {
      window.removeEventListener('online', fn);
      window.removeEventListener('offline', fn);
    };
  }, () => navigator.onLine);
}

/**
 * Keeps the queue moving for the signed-in user (mounted once, in the organisation's layout): sends on start, when
 * the network comes back, when the tab comes back in front, and on a timer while records wait; refreshes the HACCP
 * lists of the organisations where records were saved.
 */
export function useHaccpSyncEngine(userId: string, orgIds: readonly string[]): void {
  const qc = useQueryClient();
  const key = orgIds.join(',');
  useEffect(() => {
    const scope: Scope = { userId, orgIds: key.split(',') };
    const drain = (force: boolean) => { void haccpQueue.drain(scope, force); };
    const onSaved: Saved = (ids) => {
      for (const o of ids) void qc.invalidateQueries({ queryKey: keys.scoped(o, 'haccp') });
    };
    savedListeners.add(onSaved);
    void haccpQueue.load().then(() => drain(true));
    const online = () => drain(true);
    const visible = () => {
      if (document.visibilityState === 'visible') drain(true);
    };
    const tick = setInterval(() => {
      if (haccpQueue.snapshot().some((r) => r.userId === userId && r.state === 'pending')) drain(false);
    }, TICK_MS);
    window.addEventListener('online', online);
    window.addEventListener('focus', online);
    document.addEventListener('visibilitychange', visible);
    return () => {
      savedListeners.delete(onSaved);
      clearInterval(tick);
      window.removeEventListener('online', online);
      window.removeEventListener('focus', online);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [userId, key, qc]);
}

export interface HaccpQueueView {
  /** The user's records for this organisation, in order. */
  records: readonly QueuedRecord[];
  pending: QueuedRecord[];
  failed: QueuedRecord[];
  online: boolean;
  syncing: boolean;
  /** The last attempt could not reach the server although the phone says it is online. */
  unreachable: boolean;
  /** The session has ended: the records wait for their author to sign in again. */
  authBlocked: boolean;
}

/** The queue as the HACCP pages show it (indicator, pending records in the lists). */
export function useHaccpQueue(userId: string, orgId: string): HaccpQueueView {
  const all = useSyncExternalStore((fn) => haccpQueue.subscribe(fn), () => haccpQueue.snapshot());
  const state = useSyncExternalStore((fn) => haccpQueue.subscribe(fn), () => haccpQueue.state);
  const online = useOnline();
  return useMemo(() => {
    const records = all.filter((r) => r.userId === userId && r.orgId === orgId);
    return { records, pending: records.filter((r) => r.state === 'pending'), failed: records.filter((r) => r.state === 'failed'),
      online, syncing: state.syncing, unreachable: state.unreachable, authBlocked: state.authBlocked };
  }, [all, state, online, userId, orgId]);
}

/** Saves a record from a form through the queue (haccpQueue.submit): sent now, or kept on the phone. */
export function submitHaccpRecord<T>(n: NewRecord, orgIds: readonly string[]): Promise<SubmitResult<T>> {
  if (n.photo) void navigator.storage?.persist?.().catch(() => undefined);
  return haccpQueue.submit<T>(n, { userId: n.userId, orgIds });
}
